import {
  COVERAGE_DIMENSION_CODES,
  type CoverageDimensionCode,
  type CoverageDimensionScore,
  normalizeCoverageDimensionScores,
} from './coverageDimensions';

export interface CoverageQuestionInput {
  status: string;
  importance: string;
  category: string;
}

export interface ReadinessScores {
  coverageCompleteness: number;
  confidenceScore: number;
  unknownUnknownsLikelihood: number;
  ticketReadiness: number;
  cursorReadiness: number;
}

const CONFIDENCE_CODES: CoverageDimensionCode[] = ['COM', 'TAS', 'IMP', 'RUL', 'ACC'];
export const TICKET_READINESS_DIMENSION_CODES: CoverageDimensionCode[] = ['COM', 'TAS', 'ACC', 'DEP', 'RUL'];
export const CURSOR_READINESS_DIMENSION_CODES: CoverageDimensionCode[] = ['IMP', 'RUL', 'ACC', 'OBS', 'IMPX'];
const TICKET_CODES = TICKET_READINESS_DIMENSION_CODES;
const CURSOR_CODES = CURSOR_READINESS_DIMENSION_CODES;
const UNKNOWN_CODES: CoverageDimensionCode[] = ['KNO', 'QUE', 'RIS'];

const CONFLICT_PENALTY = 15;

function clampScore(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function meanScores(scores: CoverageDimensionScore[], codes: CoverageDimensionCode[]): number {
  const byCode = new Map(scores.map(s => [s.code, s.score]));
  const values = codes.map(code => byCode.get(code) ?? 0);
  if (values.length === 0) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function minScores(scores: CoverageDimensionScore[], codes: CoverageDimensionCode[]): number {
  const byCode = new Map(scores.map(s => [s.code, s.score]));
  const values = codes.map(code => byCode.get(code) ?? 0);
  if (values.length === 0) return 0;
  return Math.min(...values);
}

function importanceWeight(importance: string): number {
  if (importance === 'Critical') return 3;
  if (importance === 'Important') return 2;
  return 1;
}

export function computeMeanCoverageScore(dimensions: CoverageDimensionScore[]): number {
  const normalized = normalizeCoverageDimensionScores(dimensions);
  const total = normalized.reduce((sum, d) => sum + d.score, 0);
  return clampScore(total / normalized.length);
}

export function computeAverageCoverageProfile(
  profiles: CoverageDimensionScore[][],
): CoverageDimensionScore[] | null {
  if (profiles.length === 0) return null;

  const totals = new Map<CoverageDimensionCode, { sum: number; count: number }>(
    COVERAGE_DIMENSION_CODES.map(code => [code, { sum: 0, count: 0 }]),
  );

  for (const profile of profiles) {
    const normalized = normalizeCoverageDimensionScores(profile);
    for (const dimension of normalized) {
      const entry = totals.get(dimension.code)!;
      entry.sum += dimension.score;
      entry.count += 1;
    }
  }

  return COVERAGE_DIMENSION_CODES.map(code => {
    const entry = totals.get(code)!;
    const score = entry.count > 0 ? clampScore(entry.sum / entry.count) : 0;
    return { code, score };
  });
}

export function computeReadinessScores(
  dimensions: CoverageDimensionScore[],
  options?: { conflictingCriticalCount?: number },
): ReadinessScores {
  const normalized = normalizeCoverageDimensionScores(dimensions);
  const coverageCompleteness = computeMeanCoverageScore(normalized);

  const conflictPenalty = (options?.conflictingCriticalCount ?? 0) * CONFLICT_PENALTY;
  const confidenceBase = meanScores(normalized, CONFIDENCE_CODES);
  const confidenceScore = clampScore(confidenceBase - conflictPenalty);

  const unknownMean = meanScores(normalized, UNKNOWN_CODES);
  const unknownUnknownsLikelihood = clampScore(100 - unknownMean);

  const ticketMean = meanScores(normalized, TICKET_CODES);
  const ticketMin = minScores(normalized, TICKET_CODES);
  const ticketReadiness = clampScore(ticketMean * 0.7 + ticketMin * 0.3);

  const cursorMean = meanScores(normalized, CURSOR_CODES);
  const cursorMin = minScores(normalized, CURSOR_CODES);
  const cursorReadiness = clampScore(cursorMean * 0.5 + cursorMin * 0.5);

  return {
    coverageCompleteness,
    confidenceScore,
    unknownUnknownsLikelihood,
    ticketReadiness,
    cursorReadiness,
  };
}

export function computeLocalCoverageDimensions(
  questions: CoverageQuestionInput[],
): CoverageDimensionScore[] {
  const accepted = questions;

  return COVERAGE_DIMENSION_CODES.map(code => {
    const tagged = accepted.filter(q => q.category === code);
    if (tagged.length === 0) {
      return { code, score: 0 };
    }

    const totalWeight = tagged.reduce((sum, q) => sum + importanceWeight(q.importance), 0);
    const answeredWeight = tagged
      .filter(q => q.status === 'Answered')
      .reduce((sum, q) => sum + importanceWeight(q.importance), 0);

    return {
      code,
      score: totalWeight > 0 ? clampScore((answeredWeight / totalWeight) * 100) : 0,
    };
  });
}

export function buildCompletenessReasoning(
  dimensions: CoverageDimensionScore[],
  readiness: ReadinessScores,
): string {
  const normalized = normalizeCoverageDimensionScores(dimensions);
  const weakest = getWeakestCoverageDimensions(normalized, 3);
  const weakLabels = weakest.map(d => `${d.code} (${d.score}%)`).join(', ');
  return `Coverage completeness is ${readiness.coverageCompleteness}%. Weakest dimensions: ${weakLabels}. Ticket readiness ${readiness.ticketReadiness}%, Agent readiness ${readiness.cursorReadiness}%.`;
}

export function getWeakestCoverageDimensions(
  dimensions: CoverageDimensionScore[],
  count = 3,
): CoverageDimensionScore[] {
  const normalized = normalizeCoverageDimensionScores(dimensions);
  return [...normalized].sort((a, b) => a.score - b.score).slice(0, count);
}
